import { PublicReportEngineAndTrunkCompartmentSectionDto } from '../types/models.types';
import { getRatingMessage } from '@/helpers/ratingState';
import {
  mapScoreToNumber,
  mapEquipmentStatusToBadge,
  mapCoolantColorToOilColor,
  mapOilLeakStatusToBadge,
  mapHeadlightMountingStatusToBadge,
} from './mapper-helpers';
import { RatingSection } from './mapper-types';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';
import { BadgeOilStatus } from '@/components/Badge/Badge';
import { BadgeStatusType } from '@/components/Badge/BadgeTypes';

export interface MappedEngineAndTrunkSection {
  ratingSection: RatingSection;
  remarks: string[];
  engineCompartmentItems: InspectionItem[];
  engineExpertSummary?: string;
  trunkCompartmentItems: InspectionItem[];
  trunkExpertSummary?: string;
}

export const mapEngineAndTrunkSectionToUI = (
  section: PublicReportEngineAndTrunkCompartmentSectionDto | null | undefined,
): MappedEngineAndTrunkSection | null => {
  if (!section) {
    return null;
  }

  const summary = section.reportSectionSummary;
  const rating = mapScoreToNumber(summary?.score);
  const engine = section.engineCompartmentSubSection;
  const trunk = section.trunkCompartmentSubSection;

  // Map engine compartment items
  const engineCompartmentItems: InspectionItem[] = [
    {
      id: '1',
      name: 'Poziom płynu chłod.',
      status: mapEquipmentStatusToBadge(engine?.coolantLevel),
      badgeCustomText: engine?.coolantLevel,
      comments: engine?.coolantLevelRemark,
    },
    {
      id: '2',
      name: 'Poziom oleju w silniku',
      status: mapEquipmentStatusToBadge(engine?.engineOilLevel),
      badgeCustomText: engine?.engineOilLevel,
      comments: engine?.engineOilLevelRemark,
    },
    {
      id: '3',
      name: 'Zawieszka wym. Oleju',
      status: mapEquipmentStatusToBadge(engine?.oilChangeSticker),
      badgeCustomText: engine?.oilChangeSticker,
      comments: engine?.oilChangeStickerRemark,
    },
    {
      id: '4',
      name: 'Zawieszka wym. rozrządu',
      status: mapEquipmentStatusToBadge(engine?.timingBeltChangeSticker),
      badgeCustomText: engine?.timingBeltChangeSticker,
      comments: engine?.timingBeltChangeStickerRemark,
    },
    {
      id: '5',
      name: 'Widoczne wycieki oleju',
      status: mapOilLeakStatusToBadge(engine?.visibleOilLeaks),
      badgeCustomText: engine?.visibleOilLeaks,
      comments: engine?.visibleOilLeaksRemark,
    },
    {
      id: '6',
      name: 'Lakier komory silnika',
      status: mapEquipmentStatusToBadge(engine?.engineCompartmentPaint),
      badgeCustomText: engine?.engineCompartmentPaint,
      comments: engine?.engineCompartmentPaintRemark,
    },
    {
      id: '8',
      name: 'Kolor płynu chłodniczego',
      status: BadgeStatusType.Neutral,
      customBadge: mapCoolantColorToOilColor(engine?.coolantColor) ? (
        <BadgeOilStatus color={mapCoolantColorToOilColor(engine?.coolantColor)!} className="self-center" />
      ) : undefined,
      badgeCustomText: engine?.coolantColor,
      comments: engine?.coolantColorRemark,
    },
    {
      id: '9',
      name: 'Hałas z silnika',
      status: mapEquipmentStatusToBadge(engine?.engineNoise),
      badgeCustomText: engine?.engineNoise,
      comments: engine?.engineNoiseRemark,
    },
    {
      id: '10',
      name: 'Stan pasa przedniego',
      status: mapEquipmentStatusToBadge(engine?.frontBeltCondition),
      badgeCustomText: engine?.frontBeltCondition,
      comments: engine?.frontBeltConditionRemark,
    },
    {
      id: '11',
      name: 'Ślady odkręcania śrub mocujących',
      status: mapEquipmentStatusToBadge(engine?.mountingBoltsRemovalMarks),
      badgeCustomText: engine?.mountingBoltsRemovalMarks,
      comments: engine?.mountingBoltsRemovalMarksRemark,
    },
    {
      id: '12',
      name: 'Data produkcji reflektorów',
      status: mapEquipmentStatusToBadge(engine?.headlightsProductionDate),
      badgeCustomText: engine?.headlightsProductionDate,
      comments: engine?.headlightsProductionDateRemark,
    },
    {
      id: '13',
      name: 'Mocowanie reflektorów',
      status: mapHeadlightMountingStatusToBadge(engine?.headlightsMounting),
      badgeCustomText: engine?.headlightsMounting,
      comments: engine?.headlightsMountingRemark,
    },
    {
      id: '14',
      name: 'Stan elementów plastikowych (pokryw, zakrętek, itd.)',
      status: mapEquipmentStatusToBadge(engine?.plasticPartsCondition),
      badgeCustomText: engine?.plasticPartsCondition,
      comments: engine?.plasticPartsConditionRemark,
    },
    {
      id: '15',
      name: 'Pokrywa silnika i stan maty wygłuszającej',
      status: mapEquipmentStatusToBadge(engine?.engineCoverAndSoundproofingMatCondition),
      badgeCustomText: engine?.engineCoverAndSoundproofingMatCondition,
      comments: engine?.engineCoverAndSoundproofingMatConditionRemark,
    },
  ];

  // Map trunk compartment items
  const trunkCompartmentItems: InspectionItem[] = [
    {
      id: '1',
      name: 'Lakier wnętrza bagażnika',
      status: mapEquipmentStatusToBadge(trunk?.trunkCompartmentLacquerCondition),
      badgeCustomText: trunk?.trunkCompartmentLacquerCondition,
      comments: trunk?.trunkCompartmentLacquerConditionRemark,
    },
    {
      id: '2',
      name: 'Koło zapasowe/dojazdowe',
      status: mapEquipmentStatusToBadge(trunk?.spareWheel),
      badgeCustomText: trunk?.spareWheel,
      comments: trunk?.spareWheelRemark,
    },
    {
      id: '3',
      name: 'Widoczne ślady naprawy',
      status: mapEquipmentStatusToBadge(trunk?.visibleRepairTraces),
      badgeCustomText: trunk?.visibleRepairTraces,
      comments: trunk?.visibleRepairTracesRemark,
    },
    {
      id: '4',
      name: 'Podnośnik',
      badgeCustomText: trunk?.jack,
      status: mapEquipmentStatusToBadge(trunk?.jack),
      comments: trunk?.jackRemark,
    },
    {
      id: '5',
      name: 'Trójkąt',
      badgeCustomText: trunk?.triangle,
      status: mapEquipmentStatusToBadge(trunk?.triangle),
      comments: trunk?.triangleRemark,
    },
    {
      id: '6',
      name: 'Apteczka',
      badgeCustomText: trunk?.firstAidKit,
      status: mapEquipmentStatusToBadge(trunk?.firstAidKit),
      comments: trunk?.firstAidKitRemark,
    },
  ];

  return {
    ratingSection: {
      title: 'Ocena komory silnika i bagażnika',
      rating,
      ratingMessage: rating !== null ? getRatingMessage(rating) : 'Brak oceny',
      description: summary?.remarks && summary?.remarks?.length > 0 ? "Zwróć uwagę na podsumowanie wybranych zastrzeżeń dotyczących tej sekcji." : null,
      defectFree: summary?.defectFree ?? false,
    },
    remarks: summary?.remarks || [],
    engineCompartmentItems,
    engineExpertSummary: engine?.expertSummary,
    trunkCompartmentItems,
    trunkExpertSummary: trunk?.expertSummary,
  };
};
